import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  GlobalStyle,
  Page,
  Container,
  Hero,
  Eyebrow,
  HeroTitle,
  HeroMeta,
  HeroLede,
  AcceptBanner,
  Layout,
  TocWrap,
  TocTitle,
  TocList,
  TocLink,
  Content,
  Section,
  SectionTitle,
  SubTitle,
  P,
  List,
  ListItem,
  Callout,
  CriticalCallout,
  CriticalLabel,
  TableWrap,
  Table,
  ContactList,
  ContactRow,
  BackHome,
} from "./infoPageStyles";

const TOC = [
  { id: "contact", label: "1. Who to Contact" },
  { id: "response", label: "2. Response Times" },
  { id: "common", label: "3. Common Questions" },
  { id: "safety", label: "4. Safety & Emergencies" },
  { id: "business", label: "5. Business & Press" },
  { id: "details", label: "6. Company Details" },
];

export default function SupportPage() {
  const [active, setActive] = useState(TOC[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-15% 0px -70% 0px" },
    );
    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <Page>
      <GlobalStyle />

      <Hero>
        <Container>
          <Eyebrow>Support</Eyebrow>
          <HeroTitle>Contact &amp; Support</HeroTitle>
          <HeroMeta>
            <span>NUDR INC · Burbank, California</span>
          </HeroMeta>
          <HeroLede>
            However you need to reach us — a billing question, a safety concern,
            a privacy request, or a legal matter — this page tells you exactly
            where to send it so it lands with the right team first time.
          </HeroLede>
          <AcceptBanner>
            NudR is a strictly 18+ platform operated by NUDR INC. We're not an
            emergency service — if you or someone else is in immediate danger,
            contact local emergency services first.
          </AcceptBanner>
        </Container>
      </Hero>

      <Container>
        <Layout>
          <TocWrap>
            <TocTitle>On this page</TocTitle>
            <TocList>
              {TOC.map((item) => (
                <li key={item.id}>
                  <TocLink href={`#${item.id}`} $active={active === item.id}>
                    {item.label}
                  </TocLink>
                </li>
              ))}
            </TocList>
          </TocWrap>

          <Content>
            <Section id="contact">
              <SectionTitle>
                <span>01</span>Who to Contact
              </SectionTitle>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>What you need</th>
                      <th>Where to send it</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>General help &amp; account issues</td>
                      <td>
                        <a href="mailto:support@nudr.app">support@nudr.app</a>
                      </td>
                    </tr>
                    <tr>
                      <td>Billing, refunds &amp; cancellation</td>
                      <td>
                        <a href="mailto:support@nudr.app">support@nudr.app</a>
                      </td>
                    </tr>
                    <tr>
                      <td>Safety concerns &amp; reporting a member</td>
                      <td>
                        <a href="mailto:security@nudr.app">security@nudr.app</a>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        Reporting without a NudR account (impersonation, misuse
                        of your photos)
                      </td>
                      <td>
                        <a href="mailto:security@nudr.app">security@nudr.app</a>
                      </td>
                    </tr>
                    <tr>
                      <td>Child safety</td>
                      <td>
                        <a href="mailto:childsafety@nudr.app">
                          childsafety@nudr.app
                        </a>
                      </td>
                    </tr>
                    <tr>
                      <td>Privacy, data export &amp; deletion</td>
                      <td>
                        <a href="mailto:privacy@nudr.app">privacy@nudr.app</a>
                      </td>
                    </tr>
                    <tr>
                      <td>Legal &amp; law enforcement</td>
                      <td>
                        <a href="mailto:legal@nudr.app">legal@nudr.app</a>
                      </td>
                    </tr>
                    <tr>
                      <td>Everything else</td>
                      <td>
                        <a href="mailto:info@nudr.app">info@nudr.app</a>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </TableWrap>
              <Callout>
                <P>
                  <strong>Tip:</strong> email us from the address on your NudR
                  account where you can. It lets us verify who you are without a
                  round trip, which is usually the slowest part.
                </P>
              </Callout>
            </Section>

            <Section id="response">
              <SectionTitle>
                <span>02</span>Response Times
              </SectionTitle>
              <P>
                We prioritise by urgency rather than order of arrival. Safety
                matters always come first.
              </P>
              <List>
                <ListItem>
                  <strong>Safety and child safety reports</strong> — reviewed as
                  a priority, ahead of all other categories.
                </ListItem>
                <ListItem>
                  <strong>Billing and account issues</strong> — we aim to
                  respond within 5 business days.
                </ListItem>
                <ListItem>
                  <strong>Privacy and data requests</strong> — handled within
                  the timeframe required by applicable law.
                </ListItem>
                <ListItem>
                  <strong>General enquiries</strong> — as soon as we can.
                </ListItem>
              </List>
              <P>
                These are targets, not guarantees. If something is urgent, say
                so in the subject line.
              </P>
            </Section>

            <Section id="common">
              <SectionTitle>
                <span>03</span>Common Questions
              </SectionTitle>
              <P>
                A lot of questions are already answered in detail elsewhere:
              </P>
              <List>
                <ListItem>
                  <strong>How do I report or block someone?</strong> See{" "}
                  <Link to="/safety-tools">Reporting &amp; Blocking</Link>.
                </ListItem>
                <ListItem>
                  <strong>I don't have an account — how do I report?</strong>{" "}
                  Email <a href="mailto:security@nudr.app">security@nudr.app</a>
                  . See{" "}
                  <Link to="/safety-tools#non-users">
                    Reporting Without an Account
                  </Link>
                  .
                </ListItem>
                <ListItem>
                  <strong>How do I cancel or get a refund?</strong> See{" "}
                  <Link to="/refunds">Refunds &amp; Cancellation</Link>.
                </ListItem>
                <ListItem>
                  <strong>How do I delete my account?</strong> See{" "}
                  <Link to="/delete-account">Delete Your Account</Link>.
                </ListItem>
                <ListItem>
                  <strong>What are the rules?</strong> See our{" "}
                  <Link to="/guidelines">Community Guidelines</Link>.
                </ListItem>
                <ListItem>
                  <strong>What data do you hold about me?</strong> See our{" "}
                  <Link to="/privacy">Privacy Policy</Link>.
                </ListItem>
                <ListItem>
                  <strong>How do you protect children?</strong> See our{" "}
                  <Link to="/child-safety">Child Safety Standards</Link>.
                </ListItem>
                <ListItem>
                  <strong>More help</strong> — visit the{" "}
                  <Link to="/help">Help Center</Link>.
                </ListItem>
              </List>
            </Section>

            <Section id="safety">
              <SectionTitle>
                <span>04</span>Safety &amp; Emergencies
              </SectionTitle>
              <CriticalCallout>
                <CriticalLabel>If you're in immediate danger</CriticalLabel>
                <P>
                  <strong>Contact local emergency services first.</strong> In
                  the United States, call <strong>911</strong>. NudR cannot
                  dispatch emergency help. Once you're safe, report to us so we
                  can preserve evidence and act on the account.
                </P>
              </CriticalCallout>
              <P>
                US support lines, available 24/7 and independent of NudR:
              </P>
              <List>
                <ListItem>
                  <strong>988</strong> — Suicide &amp; Crisis Lifeline (call or
                  text)
                </ListItem>
                <ListItem>
                  <strong>Text HOME to 741741</strong> — Crisis Text Line
                </ListItem>
                <ListItem>
                  <strong>1-800-656-4673</strong> — RAINN, for survivors of
                  sexual assault
                </ListItem>
                <ListItem>
                  <strong>1-800-799-7233</strong> — National Domestic Violence
                  Hotline
                </ListItem>
              </List>
            </Section>

            <Section id="business">
              <SectionTitle>
                <span>05</span>Business &amp; Press
              </SectionTitle>
              <SubTitle>Law enforcement</SubTitle>
              <P>
                Agencies seeking user records should read our{" "}
                <Link to="/law-enforcement">Law Enforcement Guidelines</Link>{" "}
                before submitting a request — it sets out the legal process we
                require, what data we can and can't produce, and our emergency
                disclosure procedure.
              </P>
              <SubTitle>Press &amp; partnerships</SubTitle>
              <P>
                Media enquiries and partnership proposals:{" "}
                <a href="mailto:info@nudr.app">info@nudr.app</a>.
              </P>
            </Section>

            <Section id="details">
              <SectionTitle>
                <span>06</span>Company Details
              </SectionTitle>
              <ContactList>
                <ContactRow>
                  <b>Legal entity:</b> NUDR INC
                </ContactRow>
                <ContactRow>
                  <b>Incorporated:</b> State of Delaware, U.S.A.
                </ContactRow>
                <ContactRow>
                  <b>Registered address:</b> 1105 N Buena Vista Street, Burbank,
                  CA 91505, U.S.A.
                </ContactRow>
                <ContactRow>
                  <b>General enquiries:</b>{" "}
                  <a href="mailto:info@nudr.app">info@nudr.app</a>
                </ContactRow>
              </ContactList>

              <P style={{ marginTop: "1.4rem", fontSize: "0.85rem" }}>
                © {new Date().getFullYear()} NudR. All rights reserved.
              </P>

              <BackHome to="/">← Back to home</BackHome>
            </Section>
          </Content>
        </Layout>
      </Container>
    </Page>
  );
}
